import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { usePageTitle } from "@/lib/seo";
import { Card, Pill, Button } from "@/components/ui/Primitives";
import { Zap, Check, X, Undo2, Filter, Settings2, AlertCircle } from "lucide-react";

const TYPE_LABELS = {
  auto_dunning: "Late-rent reminder",
  auto_vendor_booking: "Vendor booking",
  auto_listing_syndication: "Listing syndication",
  auto_stripe_scheduling: "Rent reminder scheduling",
};

const STATUS_TONES = {
  pending: "amber",
  executed: "emerald",
  reverted: "slate",
  dismissed: "slate",
  failed: "rose",
};

export default function Activity() {
  usePageTitle("Activity ledger — Central Rentals");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [busy, setBusy] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter) params.set("status", statusFilter);
      if (typeFilter) params.set("type", typeFilter);
      const q = params.toString();
      const r = await api.get(`/agent/actions${q ? "?" + q : ""}`);
      setItems(r.data?.items || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [statusFilter, typeFilter]);

  const runScan = async () => {
    setBusy("scan");
    try {
      const r = await api.post("/agent/scan");
      toast.success(`Scan complete — queued ${r.data.queued}, ran ${r.data.executed}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Scan failed");
    } finally { setBusy(null); }
  };

  const approve = async (a) => {
    setBusy(a.action_id);
    try {
      await api.post(`/agent/actions/${a.action_id}/approve`);
      toast.success("Executed.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not approve");
    } finally { setBusy(null); }
  };
  const dismiss = async (a) => {
    setBusy(a.action_id);
    try {
      await api.post(`/agent/actions/${a.action_id}/dismiss`);
      toast.success("Dismissed.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not dismiss");
    } finally { setBusy(null); }
  };
  const revert = async (a) => {
    if (!window.confirm("Revert this action? This will undo whatever the Operator did.")) return;
    setBusy(a.action_id);
    try {
      await api.post(`/agent/actions/${a.action_id}/revert`);
      toast.success("Reverted.");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not revert");
    } finally { setBusy(null); }
  };

  const pending = items.filter((i) => i.status === "pending");

  return (
    <div className="space-y-6" data-testid="activity-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] font-bold text-emerald-700 mb-1 flex items-center gap-1.5">
            <Zap className="w-3.5 h-3.5" /> Smart Operator
          </div>
          <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900">Activity ledger</h1>
          <p className="text-sm text-slate-600 mt-1 max-w-xl">
            Every action the Operator takes on your behalf is logged here.
            Reversible for 24 hours after execution.
          </p>
        </div>
        <div className="flex gap-2">
          <Link to="/agent-settings"
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md border border-slate-200 text-sm font-semibold text-slate-700 hover:bg-slate-50"
            data-testid="agent-settings-link">
            <Settings2 className="w-4 h-4" /> Operator settings
          </Link>
          <Button onClick={runScan} disabled={busy === "scan"} data-testid="scan-button">
            {busy === "scan" ? "Scanning…" : "Run scan now"}
          </Button>
        </div>
      </div>

      {pending.length > 0 && (
        <Card className="p-4 border-amber-200 bg-amber-50" data-testid="pending-banner">
          <div className="flex items-center gap-2 text-amber-900">
            <AlertCircle className="w-4 h-4" />
            <span className="text-sm font-semibold">
              {pending.length} action{pending.length !== 1 ? "s" : ""} waiting for your approval
            </span>
          </div>
        </Card>
      )}

      <div className="flex items-center gap-2 flex-wrap" data-testid="activity-filters">
        <Filter className="w-4 h-4 text-slate-500" />
        {[
          { k: "", label: "All" },
          { k: "pending", label: "Pending" },
          { k: "executed", label: "Executed" },
          { k: "reverted", label: "Reverted" },
          { k: "dismissed", label: "Dismissed" },
          { k: "failed", label: "Failed" },
        ].map(({ k, label }) => (
          <button key={k || "all"} onClick={() => setStatusFilter(k)}
            data-testid={`filter-status-${k || "all"}`}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${
              statusFilter === k ? "bg-slate-900 text-white border-slate-900" : "border-slate-200 text-slate-600 hover:border-slate-300"
            }`}>{label}</button>
        ))}
        <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}
          data-testid="filter-type-select"
          className="ml-2 text-xs px-2 py-1.5 rounded-md border border-slate-200 bg-white">
          <option value="">All action types</option>
          {Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </div>

      <Card>
        {loading ? (
          <div className="p-6 text-sm text-slate-500">Loading…</div>
        ) : items.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-500" data-testid="activity-empty">
            No activity yet. Turn on Operator actions in <Link to="/agent-settings" className="text-emerald-700 font-semibold hover:underline">settings</Link>,
            then click "Run scan now".
          </div>
        ) : (
          <div className="divide-y divide-slate-100" data-testid="activity-list">
            {items.map((a) => (
              <div key={a.action_id} className="p-4" data-testid={`action-${a.action_id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Pill tone={STATUS_TONES[a.status] || "slate"}>{a.status}</Pill>
                      <span className="text-xs text-slate-500">{TYPE_LABELS[a.type] || a.type}</span>
                      <span className="text-xs text-slate-400">· {a.created_at?.slice(0, 16).replace("T", " ")}</span>
                    </div>
                    <div className="text-sm text-slate-900 font-medium">{a.preview}</div>
                    {a.subject?.label && (
                      <div className="text-xs text-slate-500 mt-0.5">Target: {a.subject.label}</div>
                    )}
                    {a.status === "failed" && a.error && (
                      <div className="text-xs text-rose-700 mt-1">Error: {a.error}</div>
                    )}
                  </div>
                  <div className="flex flex-col gap-2 items-end">
                    {a.status === "pending" && (
                      <>
                        <button onClick={() => approve(a)} disabled={busy === a.action_id}
                          data-testid={`approve-${a.action_id}`}
                          className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md bg-emerald-600 text-white text-xs font-semibold hover:bg-emerald-700 disabled:opacity-50">
                          <Check className="w-3.5 h-3.5" /> Approve
                        </button>
                        <button onClick={() => dismiss(a)} disabled={busy === a.action_id}
                          data-testid={`dismiss-${a.action_id}`}
                          className="inline-flex items-center gap-1 text-xs font-semibold text-slate-500 hover:text-slate-800">
                          <X className="w-3.5 h-3.5" /> Dismiss
                        </button>
                      </>
                    )}
                    {a.status === "executed" && a.revertable && (
                      <button onClick={() => revert(a)} disabled={busy === a.action_id}
                        data-testid={`revert-${a.action_id}`}
                        className="inline-flex items-center gap-1 px-3 py-1.5 rounded-md border border-slate-200 text-xs font-semibold text-slate-700 hover:bg-slate-50">
                        <Undo2 className="w-3.5 h-3.5" /> Revert
                      </button>
                    )}
                    {a.status === "executed" && !a.revertable && (
                      <span className="text-[11px] text-slate-400">Revert window expired</span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
